import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TimesheetDay } from '../../types/reports.types'

interface TimesheetDayCellProps {
  day: TimesheetDay | undefined
  dateStr: string
  isToday: boolean
  isWeekend: boolean
  onClick: () => void
}

const formatMinutesToHours = (minutes: number): string => {
  if (minutes === 0) return '-'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) return `${hours}`
  return `${hours}:${mins.toString().padStart(2, '0')}`
}

const getCellColorClass = (day: TimesheetDay | undefined): string => {
  if (!day) {
    return 'bg-gray-50'
  }

  // Non-working day (weekend or holiday)
  if (!day.is_working_day) {
    return 'bg-gray-100 text-gray-500'
  }

  // Working day but no check-in
  if (!day.check_in) {
    return 'bg-red-50 text-red-600'
  }

  // Late arrival
  if (day.is_late && day.late_minutes > 0) {
    return 'bg-orange-100 text-orange-700'
  }

  // Normal worked day
  if (day.worked_minutes > 0) {
    return 'bg-green-100 text-green-700'
  }

  return 'bg-gray-50'
}

export function TimesheetDayCell({
  day,
  dateStr,
  isToday,
  isWeekend,
  onClick,
}: TimesheetDayCellProps) {
  const colorClass = getCellColorClass(day)

  // Display content
  let displayContent: React.ReactNode
  if (!day) {
    displayContent = <span className="text-[10px] text-gray-400">-</span>
  } else if (!day.is_working_day) {
    displayContent = <span className="text-[10px]">В</span>
  } else if (!day.check_in && day.scheduled_start) {
    displayContent = <span className="text-[10px]">Н</span> // Неявка
  } else if (day.worked_minutes > 0) {
    displayContent = (
      <span className="text-[10px] font-medium">
        {formatMinutesToHours(day.worked_minutes)}
      </span>
    )
  } else {
    displayContent = <span className="text-[10px] text-gray-400">-</span>
  }

  // Tooltip content
  const tooltipContent = day ? (
    <div className="text-xs space-y-1">
      <div className="font-medium">{dateStr}</div>
      {day.is_working_day ? (
        <>
          <div>
            График: {day.scheduled_start || '?'} - {day.scheduled_end || '?'}
          </div>
          {day.check_in && (
            <div>
              Приход: <span className={day.is_late ? 'text-orange-500 font-medium' : ''}>{day.check_in}</span>
              {day.is_late && day.late_minutes > 0 && (
                <span className="text-orange-500"> (+{day.late_minutes} мин)</span>
              )}
            </div>
          )}
          {day.check_out && <div>Уход: {day.check_out}</div>}
          {!day.check_in && <div className="text-red-500">Нет отметки о приходе</div>}
          <div>
            Отработано: {formatMinutesToHours(day.worked_minutes)} из{' '}
            {formatMinutesToHours(day.scheduled_minutes)}
          </div>
          {day.overtime_minutes > 0 && (
            <div className="text-blue-500">
              Переработка: +{formatMinutesToHours(day.overtime_minutes)}
            </div>
          )}
        </>
      ) : (
        <div className="text-gray-500">Выходной</div>
      )}
    </div>
  ) : (
    <div className="text-xs text-gray-500">Нет данных</div>
  )

  return (
    <td
      className={cn(
        'p-0 border-l',
        isToday && 'ring-2 ring-blue-400 ring-inset',
      )}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClick}
              className={cn(
                'h-8 w-full flex items-center justify-center cursor-pointer transition-colors hover:opacity-80',
                colorClass,
                isWeekend && day?.is_working_day && 'border-l-2 border-l-orange-400',
              )}
            >
              {displayContent}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[250px]">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </td>
  )
}
